import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Menu, Transition, Dialog } from '@headlessui/react';
import {
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  Bars3Icon,
  Squares2X2Icon,
  EllipsisVerticalIcon,
  CommandLineIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

import ScrollableDropdown from '../components/ScrollableDropdown';
import FloatingMenu from '../components/FloatingMenu';
import AddTeamMembersModal from '../components/AddTeamMembersModal';

const Applications = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    version: 'v1.0.0',
    description: '',
    status: 'Active',
    themeColor: '#4f46e5',
    users: [],
    projectKey: ''
  });
  const [projectKeyStatus, setProjectKeyStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [projectKeyManuallyEdited, setProjectKeyManuallyEdited] = useState(false);
  const projectKeyCheckTimer = React.useRef(null);
  // Ref so setTimeout callbacks always see the latest editingApp id
  const editingAppRef = React.useRef(null);
  const setEditingAppAndRef = React.useCallback((app) => {
    editingAppRef.current = app;
    setEditingApp(app);
  }, []);

  const fetchApplications = React.useCallback(async () => {
    try {
      const { data } = await api.get('/api/applications');
      setApplications(data.data);
      setLoading(false);
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Network Error: Unable to connect to server. Please check your connection.');
      } else {
        toast.error('Failed to fetch applications');
      }
      setLoading(false);
    }
  }, []);

  const openAddModal = React.useCallback(() => {
    setEditingAppAndRef(null);
    setDuplicateApp(null);
    setShowDuplicateWarning(false);
    setFormData({
      name: '',
      url: '',
      version: 'v1.0.0',
      description: '',
      status: 'Active',
      themeColor: '#4f46e5',
      users: [],
      projectKey: ''
    });
    setProjectKeyStatus(null);
    setProjectKeyManuallyEdited(false);
    setDomains([]);
    setShowModal(true);
  }, [setEditingAppAndRef]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [addMembersAppId, setAddMembersAppId] = useState(null);

  // Domain Management State
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [domains, setDomains] = useState([]);
  const [editingDomain, setEditingDomain] = useState(null);
  const [domainFormData, setDomainFormData] = useState({
    name: '',
    status: 'Active',
    customFields: [] // { _id, name, type, value, isSensitive }
  });
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    type: 'Text',
    value: '',
    isSensitive: false
  });



  // eslint-disable-next-line no-unused-vars
  const [duplicateApp, setDuplicateApp] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    username: '',
    password: '',
    role: 'Employee',
    description: '',
    customRole: ''
  });

  const [showCustomRole, setShowCustomRole] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // UI State
  const [viewMode, setViewMode] = useState(location.state?.viewMode || 'list'); // 'list' | 'grid'
  const [showFilters, setShowFilters] = useState(false);

  // Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    ownership: 'all',
    version: ''
  });


  // Build management state
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedBuild, setSelectedBuild] = useState(null);
  const [buildFormData, setBuildFormData] = useState({
    version: '',
    buildNumber: '',
    description: '',
    changelog: ''
  });
  // In-form build documents (queued until save)
  const [buildDocuments, setBuildDocuments] = useState([]); // [{name, type, url, id}]
  const [tempDoc, setTempDoc] = useState({ name: '', type: 'Other', url: '' });
  const [editingDocId, setEditingDocId] = useState(null);
  const fileInputRef = useRef(null);
  const activeDomainInputRef = useRef(null);
  const activeUserInputRef = useRef(null);
  const buildVersionInputRef = useRef(null);
  const documentNameInputRef = useRef(null);

  // Confirm modals
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [appToArchive, setAppToArchive] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);

  // Archived view state
  const [showArchivedView, setShowArchivedView] = useState(false);
  const [archivedApps, setArchivedApps] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  const canManageDomains = (app, currentUser) => {
    if (!app || !currentUser?._id) return false;
    const uid = currentUser._id;
    return (
      app.developerId?._id === uid ||
      app.developerId === uid ||
      app.ownerId?._id === uid ||
      app.ownerId === uid
    );
  };


  // Legacy document modal state (kept for backward compatibility)
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentFormData, setDocumentFormData] = useState({
    name: '',
    type: 'Other',
    file: null
  });

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);


  // Handle navigation state to open modal or scroll to app
  useEffect(() => {
    if (location.state?.openModal) {
      openAddModal();
      // Clear the state to prevent reopening on state updates
      navigate(location.pathname, { replace: true, state: { ...location.state, openModal: false } });
    }

    // Scroll to selected application if passed from dashboard
    if (location.state?.scrollToApp && applications.length > 0) {
      const appElement = document.getElementById(`app-${location.state.scrollToApp}`);
      if (appElement) {
        setTimeout(() => {
          appElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          appElement.classList.add('highlight-app');
        }, 100);
      }
    }
  }, [location.state, applications, location.pathname, navigate, openAddModal]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showModal || showUserModal || showDuplicateWarning || showBuildModal || showDomainModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showUserModal, showDuplicateWarning, showBuildModal, showDomainModal]);

  // No manual focus effects needed - Headless UI handles nested focus traps correctly now


  useEffect(() => {
    const handleBugUpdated = () => {
      fetchApplications();
    };

    const handleStorage = (e) => {
      if (e.key === 'bugtracker:bug-updated') {
        handleBugUpdated();
      }
    };

    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const uploadQueuedDocsToBuild = async (appId, buildId) => {
    if (!buildDocuments || buildDocuments.length === 0) return;
    for (const d of buildDocuments) {
      if (!d.file) continue; // Skip if no file
      const fd = new FormData();
      fd.append('document', d.file);
      fd.append('name', d.name || d.file.name);
      fd.append('type', d.type || 'Other');
      try {
        await api.post(`/api/applications/${appId}/builds/${buildId}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error(`Failed to upload ${d.name || 'document'}`);
      }
    }
  };

  // Domain Management Functions
  const fetchDomains = async (appId) => {
    try {
      const { data } = await api.get(`/api/applications/${appId}/domains`);
      setDomains(data.data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
      // Don't show toast on 404/403 just fail silently or specific msg
    } finally {
    }
  };

  const handleAddDomain = () => {
    if (editingApp && !canManageDomains(editingApp, user)) {
      toast.error('You do not have permission to manage domains for this application');
      return;
    }
    setEditingDomain(null);
    setDomainFormData({
      name: '',
      status: 'Active',
      customFields: []
    });
    setNewCustomField({
      name: '',
      type: 'Text',
      value: '',
      isSensitive: false
    });
    setShowDomainModal(true);
  };



  const handleDomainSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!domainFormData.name.trim()) {
      toast.error('Domain name is required');
      return;
    }

    // Auto-add current custom field if name is present but not yet added
    let finalCustomFields = [...domainFormData.customFields];
    if (newCustomField.name.trim()) {
      finalCustomFields.push({ ...newCustomField });
      // Reset the current field since it's now being saved
      setNewCustomField({
        name: '',
        type: 'Text',
        value: '',
        isSensitive: false
      });
    }

    const dataToSave = { ...domainFormData, customFields: finalCustomFields };

    // Local update if creating new app (not yet saved)
    if (!editingApp) {
      if (editingDomain) {
        // Edit in local state
        setDomains(prev => prev.map(d =>
          (d.id === editingDomain.id || d._id === editingDomain._id)
            ? { ...dataToSave, id: editingDomain.id || editingDomain._id, _id: editingDomain._id }
            : d
        ));
        toast.success('Domain updated locally');
      } else {
        // Add to local state
        setDomains(prev => [...prev, { ...dataToSave, id: Date.now(), status: domainFormData.status || 'Active' }]);
        toast.success('Domain added locally');
      }
      setShowDomainModal(false);
      return;
    }

    try {
      if (editingApp && !canManageDomains(editingApp, user)) {
        toast.error('You do not have permission to manage domains for this application');
        return;
      }
      if (editingDomain) {
        await api.put(`/api/applications/${editingApp._id}/domains/${editingDomain._id}`, dataToSave);
        toast.success('Domain updated successfully');
      } else {
        await api.post(`/api/applications/${editingApp._id}/domains`, dataToSave);
        toast.success('Domain added successfully');
      }
      setShowDomainModal(false);
      fetchDomains(editingApp._id);
    } catch (error) {
      console.error('Error saving domain:', error);
      toast.error(error.response?.data?.message || 'Failed to save domain');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to delete this domain?')) {
      if (!editingApp) {
        setDomains(prev => prev.filter(d => (d.id !== domainId && d._id !== domainId)));
        toast.success('Domain deleted locally');
        return;
      }

      try {
        await api.delete(`/api/applications/${editingApp._id}/domains/${domainId}`);
        toast.success('Domain deleted successfully');
        fetchDomains(editingApp._id);
      } catch (error) {
        toast.error('Failed to delete domain');
      }
    }
  };

  const addCustomField = () => {
    if (!newCustomField.name.trim()) {
      toast.error('Field Name is required');
      return;
    }
    setDomainFormData(prev => ({
      ...prev,
      customFields: [...prev.customFields, { ...newCustomField }]
    }));
    setNewCustomField({
      name: '',
      type: 'Text',
      value: '',
      isSensitive: false
    });
  };

  const removeCustomField = (index) => {
    setDomainFormData(prev => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index)
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.description.trim()) {
      toast.error('Description is required');
      return;
    }

    // Project Key validation
    if (!editingApp) {
      // Creating: key is required
      if (!formData.projectKey || formData.projectKey.trim().length < 2) {
        toast.error('Project Key is required (minimum 2 characters)');
        return;
      }
    }
    // For both create and edit: block if key is taken or still checking
    if (projectKeyStatus === 'taken') {
      toast.error('Project Key is already taken. Please choose a different one.');
      return;
    }
    if (projectKeyStatus === 'checking') {
      toast.error('Please wait while we validate the Project Key...');
      return;
    }
    setIsSubmitting(true);


    try {
      if (editingApp) {
        const previousVersion = editingApp.version;
        await api.put(`/api/applications/${editingApp._id}`, formData);
        // Determine target build: if version changed, create a new build first
        let targetApp;
        if (formData.version !== previousVersion) {
          const { data } = await api.post(`/api/applications/${editingApp._id}/builds`, {
            version: formData.version,
            description: 'Version added from Application form',
            changelog: ''
          });
          targetApp = data.data;
        } else {
          const { data } = await api.get(`/api/applications/${editingApp._id}`);
          targetApp = data.data;
        }
        if (buildDocuments.length > 0) {
          const build = (targetApp.builds || []).find(b => b.version === formData.version) || (targetApp.builds || [])[0];
          if (build) {
            await uploadQueuedDocsToBuild(targetApp._id, build._id);
          }
        }
        toast.success('Application updated successfully');
      } else {
        // Include domains if creating new app
        const payload = { ...formData, domains: domains };
        const createRes = await api.post('/api/applications', payload);
        const createdApp = createRes.data?.data;
        if (createdApp && buildDocuments && buildDocuments.length > 0) {
          try {
            // Fetch to ensure we have builds with IDs
            const { data } = await api.get(`/api/applications/${createdApp._id}`);
            const freshApp = data.data;
            const build = (freshApp.builds || []).find(b => b.version === formData.version) || (freshApp.builds || [])[0];
            if (build && build._id) {
              await uploadQueuedDocsToBuild(freshApp._id, build._id);
            }
          } catch (docError) {
            console.error('Error uploading documents:', docError);
            toast.error('Application created but document upload failed');
          }
        }
        toast.success('Application added successfully');
      }

      setBuildDocuments([]);
      setTempDoc({ name: '', type: 'Other', url: '' });
      setShowModal(false);

      const wasEditing = !!editingApp;
      setEditingAppAndRef(null);
      setDuplicateApp(null);
      setShowDuplicateWarning(false);
      setFormData({
        name: '',
        url: '',
        version: 'v1.0.0',
        description: '',
        status: 'Active',
        users: [],
        projectKey: ''
      });
      setProjectKeyStatus(null);
      setProjectKeyManuallyEdited(false);

      await fetchApplications();

      // If we were editing, re-open the drawer with the updated app details
      if (wasEditing && editingApp) {
        // Find the updated app from the recently fetched list or fetch specifically
        // Since we just fetched, we can try to find it in the state if fetch completed, 
        // but state update is async. Better to fetch individual app or trust the 'response' if available. 
        // Simplest: Find it in the updated 'applications' state might be tricky due to closures.
        // Let's rely on api call to get fresh data for drawer
      }
    } catch (error) {
      // Check if it's a duplicate application name error (409 status)
      if (error.response?.status === 409 && error.response?.data?.isDuplicate) {
        setDuplicateApp(error.response.data.existingApp);
        setShowDuplicateWarning(true);
        // Don't show toast error here - let the confirmation modal handle it
      } else if (error.response?.status === 409 && error.response?.data?.isDuplicateKey) {
        // Duplicate project key
        setProjectKeyStatus('taken');
        toast.error(error.response.data.message || 'Project Key already in use. Please choose a different key.');
      } else {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          toast.error('Network Error: Unable to connect to server. Please check your connection.');
        } else {
          toast.error(error.response?.data?.message || 'Failed to save application');
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleEdit = (e, app) => {
    e.stopPropagation(); // Prevent navigation when clicking edit button

    setEditingAppAndRef(app);
    setFormData({
      name: app.name,
      url: app.url,
      version: app.version,
      description: app.description,
      status: app.status,
      themeColor: app.themeColor || '#4f46e5',
      users: app.users || [],
      projectKey: app.projectKey || app.code || ''
    });
    setProjectKeyStatus(null);
    setProjectKeyManuallyEdited(false);

    // Always fetch domains when editing
    fetchDomains(app._id);

    setShowModal(true);
  };

  const handleRestore = async (id, toastId) => {
    try {
      await api.put(`/api/applications/${id}/restore`, {});
      toast.dismiss(toastId);
      toast.success('Application restored');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to restore application');
    }
  };

  const fetchArchivedApplications = React.useCallback(async () => {
    setArchivedLoading(true);
    try {
      const { data } = await api.get('/api/applications/archived');
      setArchivedApps(data.data);
    } catch (error) {
      toast.error('Failed to fetch archived applications');
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  const handleArchiveApp = async (app) => {
    try {
      await api.put(`/api/applications/${app._id}/archive`, {});
      toast.success('Application archived successfully');
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to archive application');
    }
  };

  const handleUnarchiveApp = async (appId) => {
    try {
      await api.put(`/api/applications/${appId}/unarchive`, {});
      toast.success('Application restored successfully');
      setArchivedApps(prev => prev.filter(a => a._id !== appId));
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to restore application');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      // Optimistically remove from UI
      // Optimistically remove from UI
      setApplications(prev => prev.filter(app => app._id !== id));

      // Close drawer/modal if open
      // Close drawer/modal if open
      if (editingApp && editingApp._id === id) {
        setShowModal(false);
        setEditingAppAndRef(null);
      }

      try {
        await api.delete(`/api/applications/${id}`);

        // Show Undo toast
        toast((t) => (
          <div className="flex items-center gap-2">
            <span>Application deleted</span>
            <button
              onClick={() => handleRestore(id, t.id)}
              className="bg-gray-800 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
            >
              Undo
            </button>
          </div>
        ), { duration: 5000 });

      } catch (error) {
        toast.error('Failed to delete application');
        // Revert optimistic update on failure (simplified: just refetch)
        fetchApplications();
      }
    }
  };

  // User management functions
  const handleAddUser = () => {

    setEditingUser(null);
    setUserFormData({
      username: '',
      password: '',
      role: 'Employee',
      description: '',
      customRole: ''
    });
    setShowCustomRole(false);
    setShowUserModal(true);
  };



  const handleUserSubmitLocal = () => {
    // Client-side validation
    if (!userFormData.username?.trim()) {
      toast.error('Username is required');
      return;
    }
    if (!userFormData.password?.trim()) {
      toast.error('Password is required');
      return;
    }
    if (userFormData.role === 'Other' && !userFormData.customRole?.trim()) {
      toast.error('Custom Role is required when "Other" is selected');
      return;
    }

    const finalRole = userFormData.role === 'Other' ? userFormData.customRole : userFormData.role;
    const userData = {
      username: userFormData.username.trim(),
      password: userFormData.password.trim(),
      role: finalRole,
      description: (userFormData.description || '').trim()
    };

    const finalizeAddUser = (newUsers) => {
      setFormData(prev => ({ ...prev, users: newUsers }));

      // Sequence state updates to be safe
      setTimeout(() => {
        setShowUserModal(false);
        setEditingUser(null);
        // Reset local user form
        setUserFormData({
          username: '',
          password: '',
          role: 'Employee',
          description: '',
          customRole: ''
        });
        setShowCustomRole(false);
        toast.success(editingUser !== null ? 'User updated successfully' : 'User added successfully');
      }, 0);
    };

    if (editingApp && !showModal) {
      // Direct backend update for drawer mode
      const currentUsers = [...(formData.users || [])];
      const newUsers = editingUser !== null
        ? currentUsers.map((u, i) => i === editingUser ? userData : u)
        : [...currentUsers, userData];

      const saveToBackend = async () => {
        try {
          await api.put(`/api/applications/${editingApp._id}`, {
            ...formData,
            users: newUsers
          });

          setApplications(prev => prev.map(app =>
            app._id === editingApp._id ? { ...app, users: newUsers } : app
          ));
          finalizeAddUser(newUsers);
        } catch (error) {
          toast.error('Failed to save user changes');
        }
      };
      saveToBackend();
    } else {
      // Local form state update
      const currentUsers = [...(formData.users || [])];
      const newUsers = editingUser !== null
        ? currentUsers.map((u, i) => i === editingUser ? userData : u)
        : [...currentUsers, userData];
      finalizeAddUser(newUsers);
    }
  };

  const handleDeleteUser = async (index) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      const currentUsers = formData.users;
      const newUsers = currentUsers.filter((_, i) => i !== index);

      setFormData({ ...formData, users: newUsers });
    }
  };

  const handleDeleteUserInModal = async () => {
    if (editingUser === null) return;

    if (window.confirm('Are you sure you want to delete this user?')) {
      const newUsers = formData.users.filter((_, i) => i !== editingUser);

      if (editingApp && !showModal) {
        try {
          await api.put(`/api/applications/${editingApp._id}`, { ...formData, users: newUsers });
          toast.success('User deleted successfully');

          setApplications(prev => prev.map(app =>
            app._id === editingApp._id ? { ...app, users: newUsers } : app
          ));

          setFormData({ ...formData, users: newUsers });
          setShowUserModal(false);
        } catch (error) {
          toast.error('Failed to delete user');
          return;
        }
      } else {
        setFormData({ ...formData, users: newUsers });
        setShowUserModal(false);
      }

      setEditingUser(null);
      setUserFormData({
        username: '',
        password: '',
        role: 'Employee',
        description: '',
        customRole: ''
      });
      setShowCustomRole(false);
    }
  };

  // Build management functions
  const openBuildModal = (app, build = null) => {
    setSelectedApp(app);
    setSelectedBuild(build); // Set the selected build for document uploads
    if (build) {
      // Editing existing build
      setBuildFormData({
        version: build.version,
        buildNumber: build.buildNumber || '',
        description: build.description || '',
        changelog: build.changelog || ''
      });
      // Load existing documents
      setBuildDocuments(
        (build.documents || []).map((doc, idx) => ({
          id: doc._id || `existing-${idx}`,
          name: doc.name,
          type: doc.type,
          file: null, // Existing files don't have File objects
          _id: doc._id // Keep reference to existing document
        }))
      );
    } else {
      // Adding new build
      setBuildFormData({
        version: '',
        buildNumber: '',
        description: '',
        changelog: ''
      });
      setBuildDocuments([]);
    }
    setTempDoc({ name: '', type: 'Other', url: '' });
    setEditingDocId(null);
    setShowBuildModal(true);
  };

  const closeBuildModal = () => {
    setShowBuildModal(false);
    setSelectedApp(null);
    setSelectedBuild(null); // Clear selected build
    setBuildFormData({
      version: '',
      buildNumber: '',
      description: '',
      changelog: ''
    });
    setBuildDocuments([]);
    setTempDoc({ name: '', type: 'Other', url: '' });
    setEditingDocId(null);
  };

  const openArchiveConfirm = (app) => {
    setAppToArchive(app);
    setShowArchiveConfirm(true);
  };

  const openDeleteConfirm = (app) => {
    setAppToDelete(app);
    setShowDeleteConfirm(true);
  };

  const confirmArchive = async () => {
    if (!appToArchive) return;
    try {
      await handleArchiveApp(appToArchive);
    } catch (error) {
      // Error is handled in handleArchiveApp
    } finally {
      setShowArchiveConfirm(false);
      setAppToArchive(null);
    }
  };

  const confirmDelete = async () => {
    if (!appToDelete) return;
    try {
      await handleDelete(appToDelete._id);
      // handleDelete already shows feedback and updates state
    } finally {
      setShowDeleteConfirm(false);
      setAppToDelete(null);
    }
  };

  const handleBuildSubmit = async (e) => {
    e.preventDefault();

    if (!buildFormData.version.trim()) {
      toast.error('Version is required');
      return;
    }

    try {
      // Check if we're editing an existing build using selectedBuild
      let buildIdForUpload;

      if (selectedBuild && selectedBuild._id) {
        // Update existing build
        await api.put(
          `/api/applications/${selectedApp._id}/builds/${selectedBuild._id}`,
          buildFormData
        );
        buildIdForUpload = selectedBuild._id;
        toast.success('Build updated successfully');
      } else {
        // Create new build
        const response = await api.post(`/api/applications/${selectedApp._id}/builds`, buildFormData);
        buildIdForUpload = response.data.data.builds[response.data.data.builds.length - 1]._id;
        toast.success('Build added successfully');
      }

      // Upload queued documents if any
      if (buildDocuments && buildDocuments.length > 0 && buildIdForUpload) {
        await uploadQueuedDocsToBuild(selectedApp._id, buildIdForUpload);
      }

      fetchApplications();
      closeBuildModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save build');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const openDocumentModal = (app, build) => {
    setSelectedApp(app);
    setSelectedBuild(build);
    setDocumentFormData({
      name: '',
      type: 'Other',
      file: null
    });
    setShowDocumentModal(true);
  };

  const closeDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedApp(null);
    setSelectedBuild(null);
    setDocumentFormData({
      name: '',
      type: 'Other',
      file: null
    });
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();

    if (!documentFormData.file) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('document', documentFormData.file);
    formData.append('name', documentFormData.name || documentFormData.file.name);
    formData.append('type', documentFormData.type);

    try {
      const { data } = await api.post(
        `/api/applications/${selectedApp._id}/builds/${selectedBuild._id}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update the applications state with the returned updated application
      if (data.data) {
        setApplications(prev =>
          prev.map(app => app._id === data.data._id ? data.data : app)
        );
      }

      toast.success('Document uploaded successfully');
      closeDocumentModal();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload document');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setDocumentFormData({
      ...documentFormData,
      file: file,
      name: documentFormData.name || (file ? file.name : '')
    });
  };

  // eslint-disable-next-line no-unused-vars
  const downloadDocument = async (appId, buildId, documentId, filename) => {
    try {
      const response = await api.get(
        `/api/applications/${appId}/builds/${buildId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const downloadAllDocuments = async (appId, buildId, versionNumber) => {
    try {
      toast.loading('Preparing download...');
      const response = await api.get(
        `/api/applications/${appId}/builds/${buildId}/documents/download-all`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${versionNumber}-documents.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Documents downloaded successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to download documents');
    }
  };

  const deleteDocument = async (appId, buildId, documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await api.delete(`/api/applications/${appId}/builds/${buildId}/documents/${documentId}`);
        toast.success('Document deleted successfully');
        fetchApplications();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to delete document');
      }
    }
  };

  // Build document management functions
  const handleAddBuildDocument = () => {
    if (!tempDoc.file) {
      toast.error('Please select a file');
      return;
    }
    if (!tempDoc.name.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    if (editingDocId) {
      // Update existing document
      setBuildDocuments(buildDocuments.map(doc =>
        doc.id === editingDocId
          ? { ...doc, name: tempDoc.name, type: tempDoc.type, file: tempDoc.file }
          : doc
      ));
      setEditingDocId(null);
    } else {
      // Add new document
      setBuildDocuments([...buildDocuments, { ...tempDoc, id: Date.now() }]);
    }

    setTempDoc({ name: '', type: 'Other', file: null });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEditBuildDocument = (doc) => {
    setEditingDocId(doc.id);
    setTempDoc({ name: doc.name, type: doc.type, file: doc.file || null });
  };

  const handleDeleteBuildDocument = (docId) => {
    const docToDelete = buildDocuments.find(doc => doc.id === docId);
    // If it's an existing document (has _id), delete it from backend
    if (docToDelete && docToDelete._id && selectedApp && selectedApp.builds) {
      const build = selectedApp.builds.find(b => b.documents?.some(d => d._id === docToDelete._id));
      if (build) {
        deleteDocument(selectedApp._id, build._id, docToDelete._id);
      }
    }
    // Remove from local state
    setBuildDocuments(buildDocuments.filter(doc => doc.id !== docId));
  };

  const generateProjectKeySuggestion = (name) => {
    if (!name) return '';
    // Remove special chars, uppercase
    const clean = name.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    const words = clean.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) {
      // Single word: take first 3-5 uppercase letters
      return words[0].toUpperCase().substring(0, Math.min(5, words[0].length));
    }
    // Multi-word: take first letter of each word (up to 5)
    return words.slice(0, 5).map(w => w[0].toUpperCase()).join('');
  };

  const checkProjectKeyUniqueness = async (key, excludeId) => {
    if (!key || key.length < 2) {
      setProjectKeyStatus(null);
      return;
    }
    setProjectKeyStatus('checking');
    try {
      const params = { key };
      if (excludeId) params.excludeId = excludeId;
      const { data } = await api.get('/api/applications/validate-key', { params });
      if (data.available) {
        setProjectKeyStatus('available');
      } else {
        setProjectKeyStatus('taken');
      }
    } catch {
      setProjectKeyStatus(null);
    }
  };

  const handleProjectKeyChange = (value) => {
    // Allow only alphanumeric, uppercase, max 10 chars
    const sanitized = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().substring(0, 10);
    setFormData(prev => ({ ...prev, projectKey: sanitized }));
    setProjectKeyManuallyEdited(true);
    setProjectKeyStatus(null);
    // Debounce uniqueness check — read excludeId from ref to avoid stale closure
    if (projectKeyCheckTimer.current) clearTimeout(projectKeyCheckTimer.current);
    projectKeyCheckTimer.current = setTimeout(() => {
      const excludeId = editingAppRef.current?._id || null;
      checkProjectKeyUniqueness(sanitized, excludeId);
    }, 500);
  };




  // Jira-style Logic

  const THEME_COLORS = [
    { name: 'Indigo', value: '#4f46e5' },
    { name: 'Emerald', value: '#10b981' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Violet', value: '#8b5cf6' },
    { name: 'Rose', value: '#f43f5e' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Slate', value: '#64748b' },
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ status: 'all', ownership: 'all', version: '' });
    setSearchTerm('');
  };

  const filteredApplications = useMemo(() => {
    // Filter first
    const filtered = applications.filter(app => {
      // Search
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        app.name.toLowerCase().includes(term) ||
        (app.description || '').toLowerCase().includes(term) ||
        (app.developerId?.name || '').toLowerCase().includes(term);

      // Status Filter
      const matchesStatus = filters.status === 'all' || app.status === filters.status;

      // Ownership Filter
      let matchesOwnership = true;
      if (filters.ownership === 'mine') {
        matchesOwnership = app.developerId?._id === user?._id;
      } else if (filters.ownership === 'others') {
        matchesOwnership = app.developerId?._id !== user?._id;
      }

      // Version Filter
      const matchesVersion = !filters.version || (app.version.includes(filters.version));


      return matchesSearch && matchesStatus && matchesOwnership && matchesVersion;
    });

    // Then sort by name
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, searchTerm, filters, user?._id]);

  const totalPages = Math.ceil(filteredApplications.length / pageSize);
  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredApplications.slice(startIndex, startIndex + pageSize);
  }, [filteredApplications, currentPage, pageSize]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    navigate(location.pathname, { replace: true, state: { ...location.state, viewMode: mode } });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Title & Count */}
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {showArchivedView ? 'Archived Applications' : 'Applications'}
              </h1>
              <span className={`${showArchivedView ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'} py-0.5 px-2 rounded-full text-xs font-semibold`}>
                {showArchivedView ? archivedApps.length : filteredApplications.length}
              </span>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex-1 md:w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search apps, devs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-1 focus:ring-indigo-500 text-sm transition-colors"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-2.5 flex items-center"
                    title="Clear search"
                  >
                    <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors" />
                  </button>
                )}
              </div>

              {/* Primary Filters: Status */}
              <div className="hidden lg:block">
                <div className="w-40">
                  <ScrollableDropdown
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                      { value: 'Testing', label: 'Testing' },
                    ]}
                    placeholder="All Statuses"
                    buttonHeightClassName="h-10"
                    maxMenuHeightPx={240}
                    maxMenuHeightClassName="max-h-60"
                    buttonClassName="bg-gray-50 text-sm"
                  />
                </div>
              </div>

              {/* Primary Filters: Ownership */}
              {user?.role !== 'developer' && (
                <div className="hidden lg:block">
                  <div className="w-44">
                    <ScrollableDropdown
                      value={filters.ownership}
                      onChange={(value) => handleFilterChange('ownership', value)}
                      options={[
                        { value: 'all', label: 'All Ownership' },
                        { value: 'mine', label: 'Owned by me' },
                        { value: 'others', label: 'Owned by others' },
                      ]}
                      placeholder="All Ownership"
                      buttonHeightClassName="h-10"
                      maxMenuHeightPx={240}
                      maxMenuHeightClassName="max-h-60"
                      buttonClassName="bg-gray-50 text-sm"
                    />
                  </div>
                </div>
              )}

              {/* Filter Toggle */}
              {user?.role !== 'developer' && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-bold transition-all ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'bg-white border-gray-300 text-gray-700'} hover:bg-gray-50`}
                  title="More Filters"
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">More Filters</span>
                </button>
              )}

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                <button
                  onClick={() => handleViewModeChange('list')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="List View"
                >
                  <Bars3Icon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  title="Grid View"
                >
                  <Squares2X2Icon className="h-5 w-5" />
                </button>
              </div>

              {/* Archived Applications Button */}
              <button
                onClick={() => {
                  if (!showArchivedView) {
                    fetchArchivedApplications();
                  }
                  setShowArchivedView(v => !v);
                }}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-bold transition-all ${
                  showArchivedView
                    ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                title={showArchivedView ? 'Back to Applications' : 'Archived Applications'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8M10 12v4m4-4v4" />
                </svg>
                <span className="hidden sm:inline">{showArchivedView ? 'Back to Applications' : 'Archived Applications'}</span>
              </button>

              {/* Add App Button */}
              {user?.role === 'developer' && !showArchivedView && (
                <button
                  onClick={openAddModal}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Add Application</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel (More Filters) */}
        {showFilters && (
          <div className="border-t border-gray-200 bg-gray-50/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="lg:hidden">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Status</label>
                  <ScrollableDropdown
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    options={[
                      { value: 'all', label: 'All Statuses' },
                      { value: 'Active', label: 'Active' },
                      { value: 'Inactive', label: 'Inactive' },
                      { value: 'Testing', label: 'Testing' },
                    ]}
                    placeholder="All Statuses"
                    buttonHeightClassName="h-9"
                    maxMenuHeightPx={240}
                    maxMenuHeightClassName="max-h-60"
                    buttonClassName="text-sm"
                  />
                </div>
                <div className="lg:hidden">
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Ownership</label>
                  <ScrollableDropdown
                    value={filters.ownership}
                    onChange={(value) => handleFilterChange('ownership', value)}
                    options={[
                      { value: 'all', label: 'All Owners' },
                      { value: 'mine', label: 'Owned by me' },
                      { value: 'others', label: 'Owned by others' },
                    ]}
                    placeholder="All Owners"
                    buttonHeightClassName="h-9"
                    maxMenuHeightPx={240}
                    maxMenuHeightClassName="max-h-60"
                    buttonClassName="text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Version</label>
                  <input
                    type="text"
                    placeholder="e.g. v1.0"
                    value={filters.version}
                    onChange={(e) => handleFilterChange('version', e.target.value)}
                    className="block w-full py-1.5 px-3 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md shadow-sm"
                  />
                </div>
                <div className="flex flex-col justify-between">
                  <div className="flex items-end justify-between">
                    <div className="text-xs text-gray-500 italic">
                      Showing {filteredApplications.length} apps
                    </div>
                    <button
                      onClick={clearFilters}
                      className="text-xs font-medium text-indigo-600 hover:text-indigo-500"
                    >
                      Clear all filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Archived Applications View */}
        {showArchivedView ? (
          <div>
            {/* Removed internal heading to use the main page header instead */}
            {archivedLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
              </div>
            ) : archivedApps.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-lg border-2 border-dashed border-gray-200">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-amber-50 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8l1 12a2 2 0 002 2h8a2 2 0 002-2L19 8M10 12v4m4-4v4" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900">No archived applications</h3>
                <p className="mt-1 text-gray-500">You haven't archived any applications yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {archivedApps.map((app) => (
                  <div
                    key={app._id}
                    className="bg-white rounded-xl shadow-sm border border-amber-100 px-5 py-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 w-full"
                  >
                    <div className="flex items-start lg:items-center gap-3 min-w-0 flex-1">
                      <div
                        className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0 shadow-sm opacity-60"
                        style={{ backgroundColor: app.themeColor || '#4f46e5' }}
                      >
                        {app.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-base font-bold text-gray-700 leading-tight truncate">{app.name}</h3>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                            Archived
                          </span>
                        </div>
                        <a
                          href={app.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-400 hover:text-indigo-600 hover:underline truncate block max-w-md mb-1"
                        >
                          {app.url}
                        </a>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500">
                          <span className="font-mono">{app.version}</span>
                          <span className="text-gray-300">•</span>
                          <span>Dev: {app.developerId?.name || 'Unknown'}</span>
                          {app.archivedAt && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span>Archived {new Date(app.archivedAt).toLocaleDateString()}</span>
                            </>
                          )}
                          {app.archivedBy?.name && (
                            <>
                              <span className="text-gray-300">•</span>
                              <span>by {app.archivedBy.name}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end lg:self-center">
                      <button
                        onClick={() => handleUnarchiveApp(app._id)}
                        className="h-8 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-lg border-2 border-dashed border-gray-200 shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-gray-50 rounded-full">
                <CommandLineIcon className="h-10 w-10 text-gray-400" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900">No applications found</h3>
            <p className="mt-1 text-gray-500 max-w-sm mx-auto">
              {searchTerm || filters.status !== 'all' || filters.ownership !== 'all' || filters.version
                ? "We couldn't find any applications matching your current filters. Try adjusting your search or clearing filters."
                : "There are no applications currently being tracked."
              }
            </p>
            {searchTerm || filters.status !== 'all' || filters.ownership !== 'all' || filters.version ? (
              <button
                onClick={clearFilters}
                className="mt-6 inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-500"
              >
                Clear all filters
              </button>
            ) : (
              user?.role === 'developer' && (
                <div className="mt-6">
                  <button
                    onClick={openAddModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                    Add Application
                  </button>
                </div>
              )
            )}
          </div>
        ) : (
          <>
            {viewMode === 'list' ? (
              <div className="space-y-3">
                {paginatedApplications.map((app) => (
                  <div
                    key={app._id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 hover:shadow-md transition-shadow group w-full"
                  >
                    {/* Left Section: Info */}
                    <div className="flex items-start lg:items-center gap-3 min-w-0 flex-1">
                      {/* App Icon */}
                      <div
                        className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold text-base shrink-0 shadow-sm"
                        style={{ backgroundColor: app.themeColor || '#4f46e5' }}
                      >
                        {app.name.charAt(0).toUpperCase()}
                      </div>

                      {/* Text Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="text-base font-bold text-gray-900 leading-tight group-hover:text-indigo-600 transition-colors truncate">
                            {app.name}
                          </h3>
                          {(app.projectKey || app.code) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 uppercase tracking-wide border border-indigo-200 shrink-0 font-mono" title="Project Key (Bug ID prefix)">
                              {app.projectKey || app.code}
                            </span>
                          )}
                          {(app.developerId?._id === user?._id || app.ownerId?._id === user?._id || app.developerId === user?._id || app.ownerId === user?._id) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-600 uppercase tracking-wide border border-gray-200 shrink-0">
                              YOU
                            </span>
                          )}
                        </div>

                        <a
                          href={app.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-gray-400 hover:text-indigo-600 hover:underline truncate block max-w-md mb-1.5"
                        >
                          {app.url}
                        </a>

                        {/* Metadata Row */}
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 font-medium whitespace-nowrap overflow-hidden">
                          <span className="uppercase tracking-wide text-gray-400 text-[10px]">VERSION</span>
                          <span className="font-mono text-gray-600">{app.version}</span>

                          <span className="text-gray-300 mx-1">•</span>

                          <div className="flex items-center gap-1.5">
                            <span className={`h-1.5 w-1.5 rounded-full ${app.status === 'Active' ? 'bg-green-500' : app.status === 'Inactive' ? 'bg-gray-400' : 'bg-blue-500'}`} />
                            <span className={app.status === 'Active' ? 'text-gray-700' : ''}>{app.status}</span>
                          </div>

                          <span className="text-gray-300 mx-1">•</span>

                          <span className="uppercase tracking-wide text-gray-400 text-[10px]">DEV:</span>
                          <span className="truncate max-w-[120px]">{app.developerId?.name || 'Unknown'}</span>

                          <span className="text-gray-300 mx-1">•</span>

                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1" title="Open Bugs">
                              <span className="text-red-500 text-[10px]">🔴</span>
                              <span className="text-gray-700">{app.bugStats?.open || 0}</span>
                            </div>
                            <div className="flex items-center gap-1" title="Closed Bugs">
                              <span className="text-emerald-500 text-[10px]">🟢</span>
                              <span className="text-gray-700">{app.bugStats?.closed || 0}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Section: Actions */}
                    <div className="flex items-center gap-2 self-end lg:self-center" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/applications/${app._id}/timeline`)}
                        className="h-8 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap flex items-center justify-center"
                      >
                        Timeline
                      </button>

                      <button
                        onClick={() => navigate(`/applications/${app._id}`)}
                        className="h-8 px-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap flex items-center justify-center"
                      >
                        Details
                      </button>

                      {user?.role === 'developer' && (
                        <button
                          onClick={() => {
                            setAddMembersAppId(app._id);
                            setShowAddMembersModal(true);
                          }}
                          className="h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap flex items-center justify-center"
                        >
                          Add Members
                        </button>
                      )}

                      {(app.developerId?._id === user?._id || app.ownerId?._id === user?._id || app.developerId === user?._id || app.ownerId === user?._id) && (
                        <button
                          onClick={(e) => handleEdit(e, app)}
                          className="h-8 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium transition-colors shadow-sm whitespace-nowrap flex items-center justify-center"
                        >
                          Edit Application
                        </button>
                      )}

                      <FloatingMenu
                        button={
                          <button className="h-8 w-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 border border-gray-200 transition-colors" title="More options">
                            <EllipsisVerticalIcon className="h-4 w-4" />
                          </button>
                        }
                      >
                        <div className="py-1">
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => navigate(`/developer/report-bug?application=${app._id}`)}
                                className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                              >
                                Report Bug
                              </button>
                            )}
                          </Menu.Item>

                          {(app.developerId?._id === user?._id || app.ownerId?._id === user?._id || app.developerId === user?._id || app.ownerId === user?._id) && (
                            <>
                              <div className="border-t border-gray-100 my-1"></div>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => openBuildModal(app)}
                                    className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                  >
                                    Add Build
                                  </button>
                                )}
                              </Menu.Item>
                              <div className="border-t border-gray-100 my-1"></div>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => openArchiveConfirm(app)}
                                    className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                  >
                                    Archive
                                  </button>
                                )}
                              </Menu.Item>
                              <div className="border-t border-gray-100 my-1"></div>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleDelete(app._id)}
                                    className={`${active ? 'bg-red-50 text-red-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                  >
                                    Delete
                                  </button>
                                )}
                              </Menu.Item>
                            </>
                          )}
                        </div>
                      </FloatingMenu>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedApplications.map((app) => (
                  <div
                    key={app._id}
                    id={`app-${app._id}`}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 group"
                  >
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-6">
                        <div className="flex gap-4 items-start">
                          <div
                            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-sm"
                            style={{ backgroundColor: app.themeColor || '#4f46e5' }}
                          >
                            {app.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <button
                              onClick={() => navigate(`/applications/${app._id}`)}
                              className="text-lg font-bold text-gray-900 leading-tight hover:text-indigo-600 hover:underline text-left"
                            >
                              {app.name}
                            </button>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-block h-2 w-2 rounded-full ${app.status === 'Active' ? 'bg-green-500' : app.status === 'Inactive' ? 'bg-gray-300' : 'bg-blue-500'}`} />
                              <span className="text-xs text-gray-500 font-medium">{app.status}</span>
                              <span className="text-xs text-gray-300">•</span>
                              <span className="text-xs text-gray-500">{app.developerId?.name || 'Unknown'}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-4 mb-6">
                        <p className="text-sm text-gray-600 line-clamp-2 min-h-[2.5rem] leading-relaxed">
                          {app.description || 'No description available for this application.'}
                        </p>

                        <div className="flex items-center gap-4 pt-4 border-t border-gray-50">
                          <div className="flex flex-col">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Version</span>
                            <span className="text-sm font-mono text-gray-700">{app.version}</span>
                          </div>
                          <div className="flex flex-col flex-1">
                            <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">URL</span>
                            <a href={app.url} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline truncate" onClick={e => e.stopPropagation()}>
                              {app.url.replace(/^https?:\/\//, '')}
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Actions Footer */}
                      <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex-1">
                          <button
                            onClick={() => navigate(`/applications/${app._id}/timeline`)}
                            className="w-full text-center inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                          >
                            View Timeline
                          </button>
                        </div>

                        <FloatingMenu
                          button={
                            <button className="flex items-center justify-center p-2 h-10 w-10 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-full transition-colors border border-gray-200" title="More options">
                              <span className="sr-only">Open options</span>
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </button>
                          }
                        >
                          <div className="py-1">
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => navigate(`/applications/${app._id}`)}
                                  className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                >
                                  View Details
                                </button>
                              )}
                            </Menu.Item>

                            {user?.role === 'developer' && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => {
                                      setAddMembersAppId(app._id);
                                      setShowAddMembersModal(true);
                                    }}
                                    className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                  >
                                    Add Members
                                  </button>
                                )}
                              </Menu.Item>
                            )}

                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => navigate(`/developer/report-bug?application=${app._id}`)}
                                  className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}
                                >
                                  Report Bug
                                </button>
                              )}
                            </Menu.Item>

                            {(app.developerId?._id === user?._id || app.ownerId?._id === user?._id || app.developerId === user?._id || app.ownerId === user?._id) && (
                              <>
                                <div className="border-t border-gray-100 my-1"></div>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button onClick={(e) => handleEdit(e, app)} className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}>Edit Application</button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button onClick={() => openBuildModal(app)} className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}>Add Build</button>
                                  )}
                                </Menu.Item>
                                <div className="border-t border-gray-100 my-1"></div>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button onClick={() => openArchiveConfirm(app)} className={`${active ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'} block w-full px-4 py-2 text-left text-sm`}>Archive</button>
                                  )}
                                </Menu.Item>
                                <div className="border-t border-gray-100 my-1"></div>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button onClick={() => openDeleteConfirm(app)} className={`${active ? 'bg-red-50 text-red-700' : 'text-red-600'} block w-full px-4 py-2 text-left text-sm`}>Delete</button>
                                  )}
                                </Menu.Item>
                              </>
                            )}
                          </div>
                        </FloatingMenu>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <AddTeamMembersModal
              isOpen={showAddMembersModal}
              onClose={() => setShowAddMembersModal(false)}
              applicationId={addMembersAppId}
              onSuccess={fetchApplications}
            />

            {/* Pagination Controls */}
            {filteredApplications.length > 0 && (
              <div className="mt-8 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 bg-white rounded-lg shadow-sm">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-bold">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-bold">{Math.min(currentPage * pageSize, filteredApplications.length)}</span> of{' '}
                      <span className="font-bold">{filteredApplications.length}</span> apps
                    </p>
                    <div className="w-36">
                      <ScrollableDropdown
                        value={String(pageSize)}
                        onChange={(value) => {
                          setPageSize(Number(value));
                          setCurrentPage(1);
                        }}
                        options={[
                          { value: '10', label: '10 per page' },
                          { value: '20', label: '20 per page' },
                          { value: '50', label: '50 per page' },
                        ]}
                        placeholder={`${pageSize} per page`}
                        buttonHeightClassName="h-8"
                        maxMenuHeightPx={200}
                        maxMenuHeightClassName="max-h-48"
                        buttonClassName="px-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ⏮
                      </button>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ◀
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) pageNum = i + 1;
                        else if (currentPage <= 3) pageNum = i + 1;
                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                        else pageNum = currentPage - 2 + i;

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                              ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-700'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ▶
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ⏭
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Transition.Root show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className={`relative ${showDomainModal || showUserModal || showBuildModal || showDuplicateWarning ? 'z-40' : 'z-50'}`}
          onClose={() => { if (!showDomainModal && !showUserModal) { setShowModal(false); setEditingAppAndRef(null); } }}
        >
          <div className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity ${showDomainModal || showUserModal || showBuildModal || showDuplicateWarning ? 'pointer-events-none opacity-0' : ''}`} />

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-gray-100 flex flex-col max-h-[90vh]">
                  <form onSubmit={handleSubmit} className="flex flex-col h-full overflow-hidden">
                    {/* Header - Fixed */}
                    <div className="bg-white px-6 py-5 border-b border-gray-100 flex items-center justify-between shrink-0">
                      <div>
                        <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                          {editingApp ? 'Edit Application' : 'Create Application'}
                        </Dialog.Title>
                        <p className="text-xs text-gray-900 mt-0.5 uppercase tracking-widest font-bold">Project Specification</p>
                      </div>
                      <button type="button" onClick={() => { setShowModal(false); setEditingAppAndRef(null); }} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                        <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-900" />
                      </button>
                    </div>

                    {/* Content Area - Scrollable */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-7 custom-scrollbar bg-gray-50/20">
                      
                      {/* Section 1: Basic Application Details */}
                      <section className="space-y-4">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <div className="h-1.5 w-1.5 bg-indigo-600 rounded-full"></div>
                          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-widest">General Information</h4>
                        </div>

                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-5">
                          <div>
                            <label htmlFor="app-name" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                              Application Name *
                            </label>
                            <input
                              id="app-name"
                              type="text"
                              required
                              autoFocus={!showDomainModal && !showUserModal && !showDuplicateWarning && !showBuildModal}
                              value={formData.name}
                              onChange={(e) => {
                                const newName = e.target.value;
                                setFormData(prev => {
                                  const updated = { ...prev, name: newName };
                                  if (!editingApp && !projectKeyManuallyEdited) {
                                    const suggested = generateProjectKeySuggestion(newName);
                                    updated.projectKey = suggested;
                                    if (projectKeyCheckTimer.current) clearTimeout(projectKeyCheckTimer.current);
                                    if (suggested && suggested.length >= 2) {
                                      projectKeyCheckTimer.current = setTimeout(() => {
                                        checkProjectKeyUniqueness(suggested, null);
                                      }, 600);
                                    } else {
                                      setProjectKeyStatus(null);
                                    }
                                  }
                                  return updated;
                                });
                              }}
                              className="block w-full border border-gray-200 rounded-xl shadow-sm py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm transition-all"
                              placeholder="e.g., Customer Portal"
                            />
                          </div>

                          <div>
                            <label htmlFor="app-url" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                              Application URL *
                            </label>
                            <input
                              id="app-url"
                              type="url"
                              required
                              value={formData.url}
                              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                              className="block w-full border border-gray-200 rounded-xl shadow-sm py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm transition-all"
                              placeholder="https://example.com"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="app-version" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                                Version *
                              </label>
                              <input
                                id="app-version"
                                type="text"
                                required
                                value={formData.version}
                                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                                className="block w-full border border-gray-200 rounded-xl shadow-sm py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm font-mono"
                                placeholder="v1.0.0"
                              />
                            </div>

                            <div>
                              <label htmlFor="app-project-key" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                                Project Key {!editingApp && '*'}
                              </label>
                              <div className="relative">
                                <input
                                  id="app-project-key"
                                  type="text"
                                  required={!editingApp}
                                  value={formData.projectKey}
                                  onChange={(e) => handleProjectKeyChange(e.target.value)}
                                  className={`block w-full border rounded-xl shadow-sm py-2.5 px-4 pr-10 focus:ring-4 focus:outline-none uppercase font-mono text-sm transition-all
                                    ${projectKeyStatus === 'taken' ? 'border-red-400 focus:ring-red-400/5' : projectKeyStatus === 'available' ? 'border-green-400 focus:ring-green-400/5' : 'border-gray-200 focus:ring-indigo-500/5 focus:border-indigo-500'}`}
                                  placeholder="e.g., PRM"
                                  maxLength={10}
                                />
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                  {projectKeyStatus === 'checking' && <div className="h-4 w-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />}
                                  {projectKeyStatus === 'available' && <div className="h-4 w-4 bg-green-500 rounded-full flex items-center justify-center text-white text-[10px]">✓</div>}
                                  {projectKeyStatus === 'taken' && <div className="h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px]">×</div>}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div>
                            <label htmlFor="app-status" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                              Status
                            </label>
                            <ScrollableDropdown
                              id="app-status"
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'Active', label: 'Active' },
                                { value: 'Inactive', label: 'Inactive' },
                                { value: 'Deprecated', label: 'Deprecated' },
                              ]}
                              buttonHeightClassName="h-11"
                              buttonClassName="rounded-xl border-gray-200 text-sm"
                            />
                          </div>
                        </div>
                      </section>

                      {/* Section 2: Optional Configuration */}
                      <section className="space-y-4">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <div className="h-1.5 w-1.5 bg-teal-500 rounded-full"></div>
                          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Environment & Access</h4>
                        </div>

                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-6">
                          {/* Theme Color */}
                          <div>
                            <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2.5 px-0.5">
                              Branding Theme
                            </label>
                            <div className="flex flex-wrap gap-3">
                              {THEME_COLORS.map((color) => (
                                <button
                                  key={color.name}
                                  type="button"
                                  onClick={() => setFormData({ ...formData, themeColor: color.value })}
                                  className={`w-8 h-8 rounded-lg focus:outline-none transition-all ${formData.themeColor === color.value ? 'ring-4 ring-indigo-500/20 scale-110 shadow-md' : 'hover:scale-105 opacity-60 hover:opacity-100'}`}
                                  style={{ backgroundColor: color.value }}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Stacked row for Domains and Users */}
                          <div className="space-y-6">
                            {/* Domains */}
                            <div className="flex flex-col bg-teal-50/10 border border-teal-100 rounded-2xl p-6 relative overflow-hidden">
                              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                                <svg className="w-16 h-16 text-teal-600" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                </svg>
                              </div>
                              
                              <div className="flex items-start justify-between mb-5">
                                <div className="pr-4">
                                  <h5 className="text-[15px] font-bold text-gray-900">Application Domains</h5>
                                  <p className="text-xs text-gray-500 mt-1">Manage infrastructure endpoints and environment-specific configs</p>
                                </div>
                                <button 
                                  type="button" 
                                  onClick={handleAddDomain} 
                                  className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 shadow-lg shadow-teal-600/20 active:scale-95 transition-all"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  Add Domain
                                </button>
                              </div>
                              
                              <div className="bg-white/60 rounded-xl min-h-[50px] max-h-[160px] overflow-y-auto custom-scrollbar border border-teal-50 shadow-inner">
                                {domains.length > 0 ? (
                                  <div className="p-2 space-y-2">
                                    {domains.map((d, i) => (
                                      <div key={i} className="flex items-center justify-between bg-white border border-teal-100/50 p-2.5 rounded-xl shadow-sm hover:border-teal-300 transition-colors">
                                        <div className="flex items-center gap-3">
                                          <div className="w-1.5 h-1.5 rounded-full bg-teal-400"></div>
                                          <span className="text-sm font-semibold text-gray-700">{d.name}</span>
                                        </div>
                                        <button type="button" onClick={() => handleDeleteDomain(d.id || d._id)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center py-6 text-center">
                                    <p className="text-xs text-teal-400/60 font-medium italic">No domains configured yet</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Users / Credentials */}
                            <div className="flex flex-col bg-indigo-50/10 border border-indigo-100 rounded-2xl p-6 relative overflow-hidden">
                              <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                                <svg className="w-16 h-16 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                </svg>
                              </div>

                              <div className="flex items-start justify-between mb-5">
                                <div className="pr-4">
                                  <h5 className="text-[15px] font-bold text-gray-900">Auth Credentials</h5>
                                  <p className="text-xs text-gray-500 mt-1">Setup test users and platform access accounts</p>
                                </div>
                                <button 
                                  type="button" 
                                  onClick={handleAddUser} 
                                  className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                  Add User
                                </button>
                              </div>
                              
                              <div className="bg-white/60 rounded-xl min-h-[50px] max-h-[160px] overflow-y-auto custom-scrollbar border border-indigo-50 shadow-inner">
                                {formData.users.length > 0 ? (
                                  <div className="p-2 space-y-2">
                                    {formData.users.map((u, i) => (
                                      <div key={i} className="flex items-center justify-between bg-white border border-indigo-100/50 p-2.5 rounded-xl shadow-sm hover:border-indigo-300 transition-colors">
                                        <div className="flex items-center gap-3">
                                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-black uppercase ${
                                            u.role === 'Admin' ? 'bg-amber-50 text-amber-600 border border-amber-100' : 'bg-gray-50 text-gray-600 border border-gray-100'
                                          }`}>
                                            {u.role?.charAt(0) || 'U'}
                                          </div>
                                          <div>
                                            <p className="text-sm font-bold text-gray-700 leading-none">{u.username}</p>
                                            <p className="text-[10px] text-gray-400 mt-1 uppercase font-black tracking-tighter">{u.role}</p>
                                          </div>
                                        </div>
                                        <button type="button" onClick={() => handleDeleteUser(i)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all">
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center py-6 text-center">
                                    <p className="text-xs text-indigo-400/60 font-medium italic">No credentials added</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Section 3: Documentation & Description */}
                      <section className="space-y-4 pb-2">
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <div className="h-1.5 w-1.5 bg-amber-400 rounded-full"></div>
                          <h4 className="text-sm font-bold text-gray-900 uppercase tracking-widest">Metadata & Docs</h4>
                        </div>

                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-5">
                          <div>
                            <label className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 px-0.5">Reference Link</label>
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <input
                                  type="url"
                                  value={tempDoc.url || ''}
                                  onChange={(e) => setTempDoc({ ...tempDoc, url: e.target.value, name: tempDoc.name || 'Release Doc' })}
                                  placeholder="Documentation URL..."
                                  className="w-full text-sm border-gray-200 rounded-xl py-2 px-4 focus:ring-4 focus:ring-amber-500/5 focus:border-amber-400 transition-all"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!tempDoc.url?.trim()) return;
                                  setBuildDocuments(prev => [...prev, { ...tempDoc, name: tempDoc.name || 'Doc', type: 'Other', id: Date.now() }]);
                                  setTempDoc({ name: '', type: 'Other', url: '' });
                                }}
                                className="bg-gray-900 text-white text-[10px] font-bold rounded-xl px-3 py-2 hover:bg-black transition-all"
                              >
                                Attach
                              </button>
                            </div>
                            {buildDocuments.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {buildDocuments.map((d, i) => (
                                    <span key={i} className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 text-[10px] font-bold px-2.5 py-1 rounded-lg border border-amber-100">
                                      {d.url}
                                      <button type="button" onClick={() => setBuildDocuments(prev => prev.filter((_, idx) => idx !== i))} className="text-amber-400 hover:text-red-500">×</button>
                                    </span>
                                  ))}
                                </div>
                            )}
                          </div>

                          <div>
                            <label htmlFor="app-description" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 px-0.5">
                              Technical Description *
                            </label>
                            <textarea
                              id="app-description"
                              rows={3}
                              required
                              value={formData.description}
                              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                              className="block w-full border border-gray-200 rounded-xl shadow-sm py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm transition-all resize-none"
                              placeholder="Brief overview of the application purpose..."
                            />
                          </div>
                        </div>
                      </section>
                    </div>

                    {/* Footer - Fixed */}
                    <div className="bg-gray-50/50 backdrop-blur-sm px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3 shrink-0">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`inline-flex items-center justify-center rounded-xl bg-indigo-600 px-8 py-2.5 text-sm font-bold text-white shadow-xl shadow-indigo-500/10 transition-all duration-200 ${isSubmitting ? 'bg-indigo-400' : 'hover:bg-indigo-700 active:scale-95'}`}
                      >
                        {isSubmitting ? 'Syncing...' : editingApp ? 'Update App' : 'Create Application'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowModal(false); setEditingAppAndRef(null); }}
                        className="px-5 py-2.5 text-sm font-bold text-gray-400 hover:text-gray-600 transition-all"
                      >
                        Cancel
                      </button>
                    </div>

                    {/* Nested Domain Modal */}
                    <Transition.Root show={showDomainModal} as={Fragment}>
                      <Dialog as="div" className="relative z-[100]" onClose={() => setShowDomainModal(false)} initialFocus={activeDomainInputRef}>
                        <Transition.Child
                          as={Fragment}
                          enter="ease-out duration-300"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="ease-in duration-200"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity backdrop-blur-sm" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-[110] overflow-y-auto">
                          <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
                            <Transition.Child
                              as={Fragment}
                              enter="ease-out duration-300"
                              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                              enterTo="opacity-100 translate-y-0 sm:scale-100"
                              leave="ease-in duration-200"
                              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                              <Dialog.Panel className="relative transform rounded-xl bg-white text-left shadow-2xl sm:my-8 sm:w-full sm:max-w-xl max-h-[90vh] flex flex-col border border-gray-100">
                                <div className="bg-white px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                                  <div>
                                    <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                      {editingDomain ? 'Edit Domain' : 'Add Domain'}
                                    </Dialog.Title>
                                    <p className="text-xs text-gray-900 mt-0.5 uppercase tracking-widest font-bold">Domain Configuration</p>
                                  </div>
                                  <button type="button" onClick={() => setShowDomainModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                                    <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-900" />
                                  </button>
                                </div>
                                <div className="flex-1 overflow-y-auto px-6 py-4">
                                  <div className="space-y-4">
                                    <div>
                                      <label htmlFor="final-domain-input" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">
                                        Domain Name *
                                      </label>
                                      <input
                                        ref={activeDomainInputRef}
                                        id="final-domain-input"
                                        name="domainName"
                                        type="text"
                                        required
                                        value={domainFormData.name}
                                        onChange={(e) => setDomainFormData({ ...domainFormData, name: e.target.value })}
                                        className="block w-full border border-gray-200 rounded-xl shadow-sm py-2.5 px-4 focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 text-sm transition-all"
                                        placeholder="e.g., Marketing, Sales, HR"
                                        autoComplete="off"
                                      />
                                    </div>
                                    <div>
                                      <label htmlFor="domain-status" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Status</label>
                                      <ScrollableDropdown
                                        id="domain-status"
                                        value={domainFormData.status}
                                        onChange={(v) => setDomainFormData({ ...domainFormData, status: v })}
                                        options={[
                                          { value: 'Active', label: 'Active' },
                                          { value: 'Inactive', label: 'Inactive' },
                                          { value: 'Deprecated', label: 'Deprecated' },
                                        ]}
                                      />
                                    </div>
                                    <div className="border-t border-gray-100 pt-4">
                                      <h4 className="text-sm font-bold text-gray-900 mb-1">Custom Fields</h4>
                                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                          <div>
                                            <ScrollableDropdown
                                              value={newCustomField.type}
                                              onChange={(v) => setNewCustomField({ ...newCustomField, type: v })}
                                              options={[
                                                { value: 'Text', label: 'Plain Text' },
                                                { value: 'URL', label: 'External URL' },
                                                { value: 'Password', label: 'Sensitive Content' },
                                                { value: 'Token', label: 'API / Auth Token' },
                                              ]}
                                            />
                                          </div>
                                          <input
                                            type="text"
                                            placeholder="Field Name"
                                            value={newCustomField.name}
                                            onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
                                            className="w-full text-sm border-gray-200 rounded-xl px-4"
                                          />
                                        </div>
                                        <div className="mb-3">
                                          <input
                                            type={newCustomField.type === 'Password' ? 'password' : 'text'}
                                            placeholder="Value"
                                            value={newCustomField.value}
                                            onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
                                            className="w-full text-sm border-gray-200 rounded-xl px-4"
                                          />
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center">
                                            <input
                                              id="new-field-sensitive"
                                              type="checkbox"
                                              checked={newCustomField.isSensitive}
                                              onChange={(e) => setNewCustomField({ ...newCustomField, isSensitive: e.target.checked })}
                                              className="h-4 w-4 text-teal-600 border-gray-300 rounded cursor-pointer"
                                            />
                                            <label htmlFor="new-field-sensitive" className="ml-2 text-xs text-gray-700 cursor-pointer">Sensitive (Masked)</label>
                                          </div>
                                          <button type="button" onClick={addCustomField} className="bg-teal-600 text-white px-4 py-2 rounded-xl text-xs font-bold shadow-sm">Add Field</button>
                                        </div>
                                      </div>
                                      
                                      {/* Fields List */}
                                      <div className="space-y-2 max-h-[300px] overflow-y-auto px-1">
                                        {domainFormData.customFields.map((field, idx) => (
                                          <div key={idx} className="flex flex-col gap-1.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm relative group">
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] font-black text-teal-600 uppercase tracking-tighter">{field.type}</span>
                                              <button
                                                type="button"
                                                onClick={() => removeCustomField(idx)}
                                                className="text-gray-300 hover:text-red-500 transition-colors"
                                              >
                                                ×
                                              </button>
                                            </div>
                                            <div>
                                              <p className="text-xs font-bold text-gray-900">{field.name}</p>
                                              <p className="text-xs text-gray-500 truncate mt-0.5">
                                                {field.isSensitive ? '••••••••' : field.value}
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3 rounded-b-xl">
                                  <button type="button" onClick={handleDomainSubmit} className="bg-teal-600 text-white px-6 py-2 rounded-xl text-sm font-bold shadow-lg">Save Domain</button>
                                  <button type="button" onClick={() => setShowDomainModal(false)} className="px-4 text-sm font-bold text-gray-400">Cancel</button>
                                </div>
                              </Dialog.Panel>
                            </Transition.Child>
                          </div>
                        </div>
                      </Dialog>
                    </Transition.Root>

                    {/* Nested User Modal */}
                    <Transition.Root show={showUserModal} as={Fragment}>
                      <Dialog as="div" className="relative z-[100]" onClose={() => setShowUserModal(false)} initialFocus={activeUserInputRef}>
                        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <div className="fixed inset-0 bg-black bg-opacity-40 transition-opacity backdrop-blur-sm" />
                        </Transition.Child>
                        <div className="fixed inset-0 z-[110] overflow-y-auto">
                          <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                              <Dialog.Panel className="relative transform rounded-2xl bg-white shadow-2xl sm:w-full sm:max-w-md border border-gray-100">
                                <div className="px-8 pt-8 pb-6">
                                  <div className="flex justify-between items-start mb-6">
                                    <div>
                                      <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">{editingUser !== null ? 'Edit User' : 'Add User'}</Dialog.Title>
                                      <p className="text-xs text-gray-900 mt-0.5 uppercase tracking-widest font-bold">Authentication Provider</p>
                                    </div>
                                    <button onClick={() => setShowUserModal(false)} className="p-1.5 hover:bg-gray-100 rounded-full"><XMarkIcon className="h-5 w-5 text-gray-400" /></button>
                                  </div>
                                  <div className="space-y-5">
                                    <div>
                                      <label htmlFor="final-user-input" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Username / Email *</label>
                                      <input
                                        ref={activeUserInputRef}
                                        id="final-user-input"
                                        type="text"
                                        required
                                        value={userFormData.username}
                                        onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                                        className="block w-full border border-gray-200 rounded-xl py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm"
                                        placeholder="e.g., test_admin"
                                      />
                                    </div>
                                    <div>
                                      <label htmlFor="modal-password" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Access Password *</label>
                                      <input
                                        id="modal-password"
                                        type="password"
                                        required
                                        value={userFormData.password}
                                        onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                                        className="block w-full border border-gray-200 rounded-xl py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm"
                                        placeholder="••••••••"
                                      />
                                    </div>
                                    <div>
                                      <label htmlFor="modal-role" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Assigned Role</label>
                                      <ScrollableDropdown
                                        id="modal-role"
                                        value={userFormData.role}
                                        onChange={(v) => {
                                          setUserFormData({ ...userFormData, role: v });
                                          setShowCustomRole(v === 'Other');
                                        }}
                                        options={[
                                          { value: 'Admin', label: 'Admin Access' },
                                          { value: 'Employee', label: 'Standard User' },
                                          { value: 'Tester', label: 'Quality Assurance' },
                                          { value: 'Client', label: 'Client View' },
                                          { value: 'Other', label: 'Custom Role...' },
                                        ]}
                                      />
                                    </div>
                                    {showCustomRole && (
                                      <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                                        <label htmlFor="modal-custom-role" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Custom Role Name *</label>
                                        <input
                                          id="modal-custom-role"
                                          type="text"
                                          required
                                          value={userFormData.customRole}
                                          onChange={(e) => setUserFormData({ ...userFormData, customRole: e.target.value })}
                                          className="block w-full border border-gray-200 rounded-xl py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm"
                                          placeholder="e.g., Manager"
                                        />
                                      </div>
                                    )}
                                    <div>
                                      <label htmlFor="modal-description" className="block text-xs font-bold text-gray-900 uppercase tracking-wider mb-1.5 px-0.5">Description</label>
                                      <input
                                        id="modal-description"
                                        type="text"
                                        value={userFormData.description}
                                        onChange={(e) => setUserFormData({ ...userFormData, description: e.target.value })}
                                        className="block w-full border border-gray-200 rounded-xl py-2.5 px-4 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 text-sm"
                                        placeholder="Optional notes..."
                                      />
                                    </div>
                                    <button type="button" onClick={handleUserSubmitLocal} className="w-full bg-indigo-600 text-white py-3 rounded-xl font-bold shadow-xl">{editingUser !== null ? 'Save Changes' : 'Apply User'}</button>
                                    {editingUser !== null && (
                                      <button type="button" onClick={handleDeleteUserInModal} className="w-full px-4 py-2 text-sm font-bold text-red-400 hover:text-red-500 transition-all">Delete User</button>
                                    )}
                                    <button type="button" onClick={() => setShowUserModal(false)} className="w-full px-4 py-2 text-sm font-bold text-gray-400 hover:text-gray-600 transition-all">Cancel</button>
                                  </div>
                                </div>
                              </Dialog.Panel>
                            </Transition.Child>
                          </div>
                        </div>
                      </Dialog>
                    </Transition.Root>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {/* Modals moved to nested location inside the Create Application Dialog.Panel above */}

      {/* Add Build Modal with File Upload */}
      <Transition.Root show={showBuildModal} as={Fragment} afterEnter={() => buildVersionInputRef.current?.focus()}>
        <Dialog as="div" className="relative z-[80]" onClose={closeBuildModal} initialFocus={buildVersionInputRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity pointer-events-none" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto pointer-events-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-2xl border border-gray-100 flex flex-col max-h-[90vh] pointer-events-auto" style={{ pointerEvents: 'auto' }}>
                  <form onSubmit={handleBuildSubmit} className="flex flex-col h-full overflow-hidden">
                    <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                      <Dialog.Title as="h3" className="text-xl font-bold text-gray-900">
                        {buildFormData.version && selectedApp?.builds?.some(b => b.version === buildFormData.version) ? 'Edit Build / Version' : 'Add Build / Version'}
                      </Dialog.Title>
                      <button type="button" onClick={closeBuildModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="build-version" className="block text-sm font-medium text-gray-700 cursor-pointer">Version <span className="text-red-500">*</span></label>
                          <input
                            ref={buildVersionInputRef}
                            id="build-version"
                            type="text"
                            required
                            value={buildFormData.version}
                            onChange={(e) => setBuildFormData({ ...buildFormData, version: e.target.value })}
                            disabled={buildFormData.version && selectedApp?.builds?.some(b => b.version === buildFormData.version)}
                            className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto ${buildFormData.version && selectedApp?.builds?.some(b => b.version === buildFormData.version)
                              ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                              : ''
                              }`}
                            style={{ pointerEvents: 'auto' }}
                            placeholder="e.g., v1.1.0"
                          />
                          {buildFormData.version && selectedApp?.builds?.some(b => b.version === buildFormData.version) && (
                            <p className="text-xs text-gray-500 mt-1">Version number cannot be changed when editing an existing build.</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="build-number" className="block text-sm font-medium text-gray-700 cursor-pointer">Build Number</label>
                          <input
                            id="build-number"
                            type="text"
                            value={buildFormData.buildNumber}
                            onChange={(e) => setBuildFormData({ ...buildFormData, buildNumber: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto"
                            style={{ pointerEvents: 'auto' }}
                            placeholder="e.g., Build 10"
                          />
                        </div>
                        <div>
                          <label htmlFor="build-description" className="block text-sm font-medium text-gray-700 cursor-pointer">Description</label>
                          <textarea
                            id="build-description"
                            rows={2}
                            value={buildFormData.description}
                            onChange={(e) => setBuildFormData({ ...buildFormData, description: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto"
                            style={{ pointerEvents: 'auto' }}
                            placeholder="Short description of this build"
                          />
                        </div>
                        <div>
                          <label htmlFor="build-changelog" className="block text-sm font-medium text-gray-700 cursor-pointer">Changelog</label>
                          <textarea
                            id="build-changelog"
                            rows={3}
                            value={buildFormData.changelog}
                            onChange={(e) => setBuildFormData({ ...buildFormData, changelog: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto"
                            style={{ pointerEvents: 'auto' }}
                            placeholder="List of fixes, features, and changes"
                          />
                        </div>

                        {/* File Upload Section */}
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-medium text-gray-900 mb-3">Upload Documents (Optional)</h4>

                          <div className="space-y-3">
                            <div>
                              <label htmlFor="build-doc-name" className="block text-sm font-medium text-gray-700 cursor-pointer">Document Name</label>
                              <input
                                id="build-doc-name"
                                type="text"
                                value={tempDoc.name}
                                onChange={(e) => setTempDoc({ ...tempDoc, name: e.target.value })}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto"
                                style={{ pointerEvents: 'auto' }}
                                placeholder="e.g., Release Notes"
                              />
                            </div>
                            <div>
                              <label htmlFor="build-doc-type" className="block text-sm font-medium text-gray-700 cursor-pointer">Document Type</label>
                              <div className="mt-1">
                                <ScrollableDropdown
                                  id="build-doc-type"
                                  value={tempDoc.type}
                                  onChange={(value) => setTempDoc({ ...tempDoc, type: value })}
                                  options={[
                                    { value: 'Bug Fix Report', label: 'Bug Fix Report' },
                                    { value: 'Release Notes', label: 'Release Notes' },
                                    { value: 'Feature Document', label: 'Feature Document' },
                                    { value: 'Technical Document', label: 'Technical Document' },
                                    { value: 'User Manual', label: 'User Manual' },
                                    { value: 'Other', label: 'Other' },
                                  ]}
                                  placeholder="Other"
                                  buttonHeightClassName="h-10"
                                  maxMenuHeightPx={240}
                                  maxMenuHeightClassName="max-h-60"
                                  buttonClassName="text-sm pointer-events-auto"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Choose File</label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                onChange={(e) => {
                                  if (e.target.files && e.target.files[0]) {
                                    setTempDoc({ ...tempDoc, file: e.target.files[0] });
                                  }
                                }}
                                className="mt-1 block w-full text-sm text-gray-700"
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.mp4,.mov,.avi,.webm,.mkv"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleAddBuildDocument}
                              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-50 text-base font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              {editingDocId ? 'Update Document' : 'Add Document'}
                            </button>
                          </div>

                          {/* Uploaded Documents List */}
                          {buildDocuments.length > 0 && (
                            <div className="mt-4">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">Uploaded Documents ({buildDocuments.length})</h5>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {buildDocuments.map((doc) => (
                                  <div key={doc.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                                      <p className="text-xs text-gray-500">{doc.type} • {doc.file?.name}</p>
                                    </div>
                                    <div className="flex gap-2 ml-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEditBuildDocument(doc)}
                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                        title="Edit"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteBuildDocument(doc.id)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                        title="Delete"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Save Build
                      </button>
                      <button
                        type="button"
                        onClick={closeBuildModal}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Upload Document Modal */}
      <Transition.Root show={showDocumentModal} as={Fragment} afterEnter={() => documentNameInputRef.current?.focus()}>
        <Dialog as="div" className="relative z-[90]" onClose={closeDocumentModal} initialFocus={documentNameInputRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity pointer-events-none" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto pointer-events-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-gray-100 flex flex-col pointer-events-auto" style={{ pointerEvents: 'auto' }}>
                  <form onSubmit={handleDocumentSubmit} className="flex flex-col h-full overflow-hidden">
                    <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                      <Dialog.Title as="h3" className="text-xl font-bold text-gray-900">Upload Build Document</Dialog.Title>
                      <button type="button" onClick={closeDocumentModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="upload-doc-name" className="block text-sm font-medium text-gray-700 cursor-pointer">Document Name</label>
                          <input
                            ref={documentNameInputRef}
                            id="upload-doc-name"
                            type="text"
                            value={documentFormData.name}
                            onChange={(e) => setDocumentFormData({ ...documentFormData, name: e.target.value })}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 pointer-events-auto"
                            style={{ pointerEvents: 'auto' }}
                            placeholder="e.g., Release Notes v1.1"
                          />
                        </div>
                        <div>
                          <label htmlFor="upload-doc-type" className="block text-sm font-medium text-gray-700 cursor-pointer">Type</label>
                          <div className="mt-1">
                            <ScrollableDropdown
                              id="upload-doc-type"
                              value={documentFormData.type}
                              onChange={(value) => setDocumentFormData({ ...documentFormData, type: value })}
                              options={[
                                { value: 'Bug Fix Report', label: 'Bug Fix Report' },
                                { value: 'Release Notes', label: 'Release Notes' },
                                { value: 'Feature Document', label: 'Feature Document' },
                                { value: 'Technical Document', label: 'Technical Document' },
                                { value: 'User Manual', label: 'User Manual' },
                                { value: 'Other', label: 'Other' },
                              ]}
                              placeholder="Other"
                              buttonHeightClassName="h-10"
                              maxMenuHeightPx={240}
                              maxMenuHeightClassName="max-h-60"
                              buttonClassName="text-sm pointer-events-auto"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">File <span className="text-red-500">*</span></label>
                          <input
                            type="file"
                            onChange={handleFileChange}
                            className="mt-1 block w-full text-sm text-gray-700 pointer-events-auto"
                            style={{ pointerEvents: 'auto' }}
                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.mp4,.mov,.avi"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-row-reverse gap-3">
                      <button
                        type="submit"
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={closeDocumentModal}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Archive Confirmation Modal */}
      <Transition.Root show={showArchiveConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={() => setShowArchiveConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-gray-100">
                  <div className="px-6 py-4">
                    <Dialog.Title className="text-lg font-bold text-gray-900">Archive Application</Dialog.Title>
                    <p className="mt-2 text-sm text-gray-700">Are you sure you want to archive this application?</p>
                    <p className="mt-1 text-xs text-gray-500">You can restore it later from the archived list.</p>
                  </div>
                  <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t">
                    <button
                      type="button"
                      onClick={() => setShowArchiveConfirm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmArchive}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
                    >
                      Archive
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Delete Confirmation Modal */}
      <Transition.Root show={showDeleteConfirm} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={() => setShowDeleteConfirm(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-md border border-gray-100">
                  <div className="px-6 py-4">
                    <Dialog.Title className="text-lg font-bold text-gray-900">Delete Application</Dialog.Title>
                    <p className="mt-2 text-sm text-gray-700">This action cannot be undone. Are you sure you want to delete this application?</p>
                  </div>
                  <div className="bg-gray-50 px-6 py-3 flex justify-end gap-3 border-t">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={confirmDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default Applications;
